import React, { useEffect, useState } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { keyframes } from '@emotion/react'
import { colors } from 'constants/colors'

/* ── animations ─────────────────────────────────────────────── */
const spinCW = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`
const spinCCW = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(-360deg); }
`
const pulse = keyframes`
  0%, 100% { transform: scale(1);    box-shadow: 0 0 12px 4px #31afb455; }
  50%       { transform: scale(1.18); box-shadow: 0 0 28px 10px #31afb488; }
`
const fadePhrase = keyframes`
  0%   { opacity: 0; transform: translateY(8px); }
  15%  { opacity: 1; transform: translateY(0); }
  80%  { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-8px); }
`
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`

const PHRASES = [
  'Pulling the picks…',
  'Decoding your rivals…',
  'Running the numbers…',
  'Who dared triple cap?',
  'Mapping the captains…',
  'Exposing the differentials…',
]

const ACCENT = colors.MOONSTONE
const DARK   = colors.ALMOST_BLACK

/* ── radar-style loader ─────────────────────────────────────── */
const RadarLoader: React.FC<{ bg: string }> = ({ bg }) => (
  <Box sx={{ position: 'relative', width: 130, height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

    {/* outermost glow halo */}
    <Box sx={{
      position: 'absolute',
      width: 130, height: 130,
      borderRadius: '50%',
      boxShadow: `0 0 32px 6px ${ACCENT}33`,
      pointerEvents: 'none',
    }} />

    {/* outer arc — conic gradient sweep */}
    <Box sx={{
      position: 'absolute',
      width: 130, height: 130,
      borderRadius: '50%',
      background: `conic-gradient(from 0deg, transparent 65%, ${ACCENT}cc 85%, ${ACCENT} 100%)`,
      animation: `${spinCW} 2s linear infinite`,
    }}>
      {/* mask the fill to create a ring */}
      <Box sx={{
        position: 'absolute',
        inset: 6,
        borderRadius: '50%',
        backgroundColor: bg,
      }} />
    </Box>

    {/* outer ring border (static, subtle) */}
    <Box sx={{
      position: 'absolute',
      width: 130, height: 130,
      borderRadius: '50%',
      border: `1.5px solid ${ACCENT}22`,
      pointerEvents: 'none',
    }} />

    {/* middle dashed counter-ring */}
    <Box sx={{
      position: 'absolute',
      width: 90, height: 90,
      borderRadius: '50%',
      border: `2px dashed ${ACCENT}55`,
      animation: `${spinCCW} 3s linear infinite`,
    }} />

    {/* inner solid ring */}
    <Box sx={{
      position: 'absolute',
      width: 60, height: 60,
      borderRadius: '50%',
      border: `2px solid transparent`,
      borderTopColor: ACCENT,
      borderRightColor: ACCENT,
      animation: `${spinCW} 1s linear infinite`,
      filter: `drop-shadow(0 0 4px ${ACCENT}99)`,
    }} />

    {/* centre pulsing ball */}
    <Box sx={{
      width: 16, height: 16,
      borderRadius: '50%',
      background: `radial-gradient(circle at 35% 35%, #7ee8ed, ${ACCENT} 60%, #005f66)`,
      animation: `${pulse} 1.6s ease-in-out infinite`,
      zIndex: 2,
    }} />
  </Box>
)

/* ── cycling phrase ─────────────────────────────────────────── */
const CyclingText: React.FC<{ color: string }> = ({ color }) => {
  const [idx, setIdx] = useState(0)
  const [key, setKey] = useState(0)

  useEffect(() => {
    const id = setInterval(() => {
      setIdx(i => (i + 1) % PHRASES.length)
      setKey(k => k + 1)
    }, 2000)
    return () => clearInterval(id)
  }, [])

  return (
    <Box sx={{ height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography
        key={key}
        fontFamily='PremierLeagueBold'
        sx={{
          color,
          fontSize: '0.95rem',
          letterSpacing: '0.06em',
          animation: `${fadePhrase} 2s ease forwards`,
        }}
      >
        {PHRASES[idx]}
      </Typography>
    </Box>
  )
}

/* ── main overlay ───────────────────────────────────────────── */
interface Props {
  loading: boolean
  hasData: boolean
  error: string
}

const RightPanelOverlay: React.FC<Props> = ({ loading, hasData, error }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const bg = theme.palette.background.paper
  const textPrimary = isDark ? '#cceef0' : DARK
  const textMuted   = isDark ? '#6dbfc4' : '#004d56'

  if (!loading && hasData && !error) return null

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: { xs: 280, sm: 400 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        py: 6,
        animation: `${fadeIn} 0.35s ease both`,
      }}
    >
      {loading ? (
        <>
          <RadarLoader bg={bg} />
          <CyclingText color={textPrimary} />
        </>
      ) : error ? (
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '2rem', mb: 1 }}>⚠️</Typography>
          <Typography fontFamily='PremierLeagueBold' color='error' fontSize='0.9rem'>
            {error}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ opacity: 0.18, fontSize: '3.5rem', lineHeight: 1, userSelect: 'none' }}>⚽</Box>
          <Typography
            fontFamily='PremierLeagueBold'
            sx={{ color: textPrimary, opacity: 0.45, fontSize: '1rem', letterSpacing: '0.05em' }}
          >
            No data yet
          </Typography>
          <Typography
            fontFamily='PremierLeagueRegular'
            sx={{ color: textMuted, opacity: 0.45, fontSize: '0.78rem' }}
          >
            Select a league & gameweek, then hit Get Insights
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default RightPanelOverlay
